'use client';

import { User } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';

import { Button } from '@/components/ui/button';

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface LegalPageProps {
  title: string;
  effectiveDate: string;
  sections: Section[];
}

export function LegalPage({ title, effectiveDate, sections }: LegalPageProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [activeSection, setActiveSection] = React.useState<string>(
    sections[0]?.id || ''
  );

  // Track active section on scroll
  React.useEffect(() => {
    const handleScroll = (e: Event) => {
      const container = e.target as HTMLElement;
      const sectionElements = sections.map((s) =>
        document.getElementById(s.id)
      );

      // Find the section currently in view
      const currentSection = sectionElements.find((el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.top <= 200 && rect.bottom >= 200;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    const scrollContainer = document.querySelector('.scroll-container');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [sections]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#131313]">
      {/* Navbar - matching your app style */}
      <nav className="flex items-center justify-between px-6 py-4 gap-4 bg-background border-b border-border">
        {/* Left Side - Logo and Breadcrumb */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            onClick={() => router.push('/')}
            className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <Image
              src="/creatr-logo.svg"
              alt="Deepbuild"
              width={60}
              height={60}
            />
          </button>
          <span className="text-gray-400 font-bold flex-shrink-0">/</span>
          <span className="text-foreground font-medium truncate">
            {pathname === '/terms' ? 'Terms & Conditions' : 'Privacy Policy'}
          </span>
        </div>

        {/* Right Side - User Icon (placeholder) */}
        <div className="flex items-center space-x-4 flex-1 justify-end flex-shrink-0">
          <Button size="default" onClick={() => router.push('/')}>
            Sign in
          </Button>
        </div>
      </nav>

      {/* Main Content with QNA-style background */}
      <main className="flex-1 min-h-0 overflow-hidden p-5">
        <div className="w-full h-full bg-matte-900 bg-[url('/noise.svg'),url('/Lights.svg')] bg-[size:auto,200%_250%] bg-[position:center,75%_50%] rounded-tl-xl rounded-tr-xl relative overflow-hidden">
          {/* Grid overlay with radial fade */}
          <div
            className="absolute inset-0 bg-[linear-gradient(to_right,#1a1a1a_1px,transparent_1px),linear-gradient(to_bottom,#1a1a1a_1px,transparent_1px)] bg-[size:42px_42px]"
            style={{
              maskImage:
                'radial-gradient(circle at center, black 0%, transparent 50%)',
              WebkitMaskImage:
                'radial-gradient(circle at center, black 0%, transparent 50%)',
            }}
          />

          {/* Scrollable Content */}
          <div className="relative z-10 h-full overflow-y-auto scroll-container">
            <div className="w-full max-w-7xl mx-auto px-4 lg:px-8 pt-12 pb-20">
              <div className="flex gap-8 lg:gap-12">
                {/* Main Content */}
                <div className="flex-1 min-w-0">
                  {/* Page Header */}
                  <div className="mb-12">
                    <h1 className="text-3xl lg:text-4xl font-semibold mb-3 text-foreground">
                      {title}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Effective Date: {effectiveDate}
                    </p>
                  </div>

                  {/* Content Sections */}
                  <div className="max-w-3xl space-y-12">
                    {sections.map((section) => (
                      <section
                        key={section.id}
                        id={section.id}
                        className="scroll-mt-20"
                      >
                        <h2 className="text-xl lg:text-2xl font-semibold text-foreground mb-4">
                          {section.title}
                        </h2>
                        <div className="prose prose-invert max-w-none">
                          <div className="text-muted-foreground leading-relaxed space-y-4 text-[15px]">
                            {section.content}
                          </div>
                        </div>
                      </section>
                    ))}
                  </div>

                  {/* Contact Footer */}
                  <div className="max-w-3xl mt-16 pt-8 border-t border-border/50">
                    <p className="text-sm text-muted-foreground">
                      Questions? Contact us at{' '}
                      <a
                        href="mailto:prince@getcreatr.com"
                        className="text-primary hover:underline"
                      >
                        prince@getcreatr.com
                      </a>
                    </p>
                  </div>
                </div>

                {/* Table of Contents - Sticky Sidebar */}
                <aside className="hidden xl:block w-64 shrink-0">
                  <div className="sticky top-8">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-4 font-medium">
                      On This Page
                    </div>
                    <nav className="space-y-1">
                      {sections.map((section, index) => (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`
                            block w-full text-left text-sm py-2 px-3 rounded-md transition-colors
                            ${
                              activeSection === section.id
                                ? 'text-primary bg-primary/10 font-medium'
                                : 'text-muted-foreground hover:text-foreground hover:bg-matte-800/50'
                            }
                          `}
                        >
                          {section.title}
                        </button>
                      ))}
                    </nav>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
